import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vendorOnboardingAPI, vendorsAPI } from '../services/api';
import { useApi } from '../hooks/useApi';
import { 
  Vendor, 
  VendorOnboardingForm, 
  FormTemplate, 
  FormField,
  VendorOnboardingWorkflow 
} from '../types';
import { 
  FileText, 
  CheckCircle, 
  XCircle, 
  Download,
  ArrowLeft,
  Send,
  Eye
} from 'lucide-react';

const VendorOnboarding: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [selectedFormType, setSelectedFormType] = useState<string>('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<VendorOnboardingForm | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: vendor } = useApi<Vendor>(
    () => vendorsAPI.getById(parseInt(vendorId!)),
    [vendorId]
  );

  const { data: templates } = useApi<FormTemplate[]>(
    () => vendorOnboardingAPI.getTemplates(),
    []
  );

  const { data: forms, loading, refetch } = useApi<VendorOnboardingForm[]>(
    () => vendorOnboardingAPI.getVendorForms(parseInt(vendorId!)),
    [vendorId]
  );

  const { data: workflow } = useApi<{ workflow: VendorOnboardingWorkflow; form_statuses: any }>(
    () => vendorOnboardingAPI.getWorkflowStatus(parseInt(vendorId!)),
    [vendorId]
  );

  const { register, handleSubmit, formState: { errors } } = useForm();

  const formTypeLabels: { [key: string]: string } = {
    'basic_info': 'Basic Information',
    'tax_info': 'Tax Information',
    'bank_info': 'Bank Information',
    'compliance': 'Compliance',
    'insurance': 'Insurance',
    'certifications': 'Certifications',
    'bcp': 'Business Continuity Plan',
    'drp': 'Disaster Recovery Plan',
    'anti_slavery': 'Anti-Slavery Policy',
    'cybersecurity': 'Cybersecurity Risk Management',
    'data_protection': 'Data Protection Policy',
    'self_certification': 'Self Certification',
    'due_diligence': 'Due Diligence Questionnaire'
  };

  const statusColors: { [key: string]: string } = {
    'draft': 'bg-gray-100 text-gray-800',
    'submitted': 'bg-primary-100 text-primary-800',
    'under_review': 'bg-yellow-100 text-yellow-800',
    'approved': 'bg-green-100 text-green-800',
    'rejected': 'bg-red-100 text-red-800'
  };

  const getFormTemplate = (formType: string): FormTemplate | undefined => {
    return templates?.find(template => template.form_type === formType);
  };

  const getFormStatus = (formType: string): string => {
    const form = forms?.find(f => f.form_type === formType);
    return form?.status || 'not_started';
  };

  const onSubmitForm = async (data: any) => {
    if (!vendorId || isSubmitting) return; // Prevent multiple submissions

    setIsSubmitting(true);
    try {
      await vendorOnboardingAPI.createForm({
        vendor_id: parseInt(vendorId),
        form_type: selectedFormType,
        form_data: data,
        status: 'draft'
      });
      setShowFormModal(false);
      refetch();
    } catch (error) {
      console.error('Error creating form:', error);
      alert('Error creating form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitForm = async (formId: number) => {
    setIsSubmitting(true);
    try {
      await vendorOnboardingAPI.submitForm(formId);
      refetch();
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewForm = async (formId: number, action: 'approve' | 'reject', comments?: string) => {
    setIsSubmitting(true);
    try {
      await vendorOnboardingAPI.reviewForm(formId, action, comments);
      setShowReviewModal(false);
      setSelectedForm(null);
      refetch();
    } catch (error) {
      console.error('Error reviewing form:', error);
      alert('Error reviewing form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPDF = async (formId: number) => {
    try {
      const response = await vendorOnboardingAPI.downloadPDF(formId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vendor-onboarding-form-${formId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/vendors')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Vendors</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Vendor Onboarding - {vendor?.name}
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            vendor?.onboarding_status === 'completed' ? 'bg-green-100 text-green-800' :
            vendor?.onboarding_status === 'in_progress' ? 'bg-primary-100 text-primary-800' :
            vendor?.onboarding_status === 'rejected' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {vendor?.onboarding_status?.replace('_', ' ').toUpperCase() || 'PENDING'}
          </span>
        </div>
      </div>

      {/* Workflow Progress */}
      {workflow && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Onboarding Progress</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {Object.keys(formTypeLabels).map((formType) => {
              const status = getFormStatus(formType);
              const isCompleted = status === 'approved';
              const isInProgress = ['draft', 'submitted', 'under_review'].includes(status);
              
              return (
                <div
                  key={formType}
                  className={`p-4 rounded-lg border-2 ${
                    isCompleted ? 'border-green-500 bg-green-50' :
                    isInProgress ? 'border-primary-500 bg-primary-50' :
                    'border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <FileText className={`h-5 w-5 ${
                      isCompleted ? 'text-green-600' :
                      isInProgress ? 'text-primary-600' :
                      'text-gray-400'
                    }`} />
                    {isCompleted && <CheckCircle className="h-5 w-5 text-green-600" />}
                    {status === 'rejected' && <XCircle className="h-5 w-5 text-red-600" />}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900">
                    {formTypeLabels[formType]}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {status.replace('_', ' ').toUpperCase()}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Forms List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Onboarding Forms</h2>
            <button
              onClick={() => setShowFormModal(true)}
              className="btn-primary"
            >
              Create New Form
            </button>
          </div>
        </div>

        <div className="p-6">
          {forms && forms.length > 0 ? (
            <div className="space-y-4">
              {forms.map((form) => (
                <div key={form.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <FileText className="h-6 w-6 text-primary-600" />
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {formTypeLabels[form.form_type]}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Created: {new Date(form.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[form.status]}`}>
                        {form.status.replace('_', ' ').toUpperCase()}
                      </span>
                      
                      <button
                        onClick={() => {
                          setSelectedForm(form);
                          setShowDetailsModal(true);
                        }}
                        className="p-2 text-gray-600 hover:text-primary-600"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      
                      {form.pdf_path && (
                        <button
                          onClick={() => handleDownloadPDF(form.id)}
                          className="p-2 text-gray-600 hover:text-primary-600"
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      
                      {form.status === 'draft' && (
                        <button
                          onClick={() => handleSubmitForm(form.id)}
                          className="p-2 text-gray-600 hover:text-green-600"
                          title="Submit for Review"
                        >
                          <Send className="h-4 w-4" />
                        </button>
                      )}
                      
                      {form.status === 'submitted' && (
                        <button
                          onClick={() => {
                            setSelectedForm(form);
                            setShowReviewModal(true);
                          }}
                          className="p-2 text-gray-600 hover:text-primary-600"
                          title="Review Form"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No forms created yet.</p>
              <button
                onClick={() => setShowFormModal(true)}
                className="mt-4 btn-primary"
              >
                Create First Form
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedFormType ? `Create ${formTypeLabels[selectedFormType]} Form` : 'Select Form Type'}
              </h3>
              <button
                onClick={() => {
                  setShowFormModal(false);
                  setSelectedFormType('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            {!selectedFormType ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates?.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedFormType(template.form_type)}
                    className="p-4 border rounded-lg hover:border-primary-500 hover:bg-primary-50 text-left"
                  >
                    <h4 className="font-medium text-gray-900">{template.name}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Status: {getFormStatus(template.form_type).replace('_', ' ').toUpperCase()}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
                {getFormTemplate(selectedFormType)?.fields.map((field: FormField) => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {field.type === 'textarea' ? (
                      <textarea
                        {...register(field.name, { required: field.required })}
                        rows={3}
                        className="input-field"
                      />
                    ) : field.type === 'select' ? (
                      <select
                        {...register(field.name, { required: field.required })}
                        className="input-field"
                      >
                        <option value="">Select {field.label}</option>
                        {field.options?.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    ) : field.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        {...register(field.name)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    ) : (
                      <input
                        type={field.type}
                        {...register(field.name, { required: field.required })}
                        className="input-field"
                      />
                    )}
                    
                    {errors[field.name] && (
                      <p className="text-red-500 text-sm mt-1">
                        {field.label} is required
                      </p>
                    )}
                  </div>
                ))}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFormModal(false);
                      setSelectedFormType('');
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Form'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Review Form Modal */}
      {showReviewModal && selectedForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Review {formTypeLabels[selectedForm.form_type]} Form
              </h3>
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedForm(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Form Data:</h4>
                <div className="space-y-2">
                  {Object.entries(selectedForm.form_data).map(([key, value]) => (
                    <div key={key} className="flex">
                      <span className="font-medium w-1/3">{key}:</span>
                      <span className="w-2/3">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => handleReviewForm(selectedForm.id, 'reject')}
                  disabled={isSubmitting}
                  className="btn-secondary"
                >
                  {isSubmitting ? 'Rejecting...' : 'Reject'}
                </button>
                <button
                  onClick={() => handleReviewForm(selectedForm.id, 'approve')}
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Form Details Modal */}
      {showDetailsModal && selectedForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {formTypeLabels[selectedForm.form_type]} - Form Details
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedForm(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Form Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Form Status</h4>
                  <p className="text-sm text-gray-500">
                    Created: {new Date(selectedForm.created_at).toLocaleDateString()}
                    {selectedForm.updated_at && selectedForm.updated_at !== selectedForm.created_at && (
                      <span> • Updated: {new Date(selectedForm.updated_at).toLocaleDateString()}</span>
                    )}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedForm.status]}`}>
                  {selectedForm.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              {/* Form Data */}
              <div className="bg-white border rounded-lg">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h4 className="font-medium text-gray-900">Submitted Information</h4>
                </div>
                <div className="p-4 space-y-4">
                  {Object.entries(selectedForm.form_data).map(([key, value]) => (
                    <div key={key} className="border-b border-gray-100 pb-3 last:border-b-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 capitalize">
                            {key.replace(/_/g, ' ')}
                          </h5>
                          <div className="mt-1 text-sm text-gray-600">
                            {typeof value === 'object' ? (
                              <pre className="whitespace-pre-wrap bg-gray-50 p-2 rounded text-xs">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (
                              <span className="break-words">
                                {String(value) || 'Not provided'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                {selectedForm.pdf_path && (
                  <button
                    onClick={() => handleDownloadPDF(selectedForm.id)}
                    className="btn-secondary flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedForm(null);
                  }}
                  className="btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorOnboarding; 